import { openNotification as openNotificationFn } from 'src/screens/shared/methods/openNotification';

type NotificationType = 'success' | 'error' | 'loading' | 'warning';

/**
 * Show a success notification
 * Replaces: openNotification('success', t('something_success'))
 * 
 * @param message - The message to display
 */
export const notifySuccess = (message: string): void => {
  openNotificationFn('success', message);
};

/**
 * Show an error notification
 * Replaces: openNotification('error', t('something_error'))
 * 
 * @param message - The message to display
 */
export const notifyError = (message: string): void => {
  openNotificationFn('error', message);
};

/**
 * Show a loading notification
 * 
 * @param message - The message to display
 */
export const notifyLoading = (message: string): void => {
  openNotificationFn('loading', message);
};

/**
 * Show a warning notification
 * 
 * @param message - The message to display
 */
export const notifyWarning = (message: string): void => {
  openNotificationFn('warning', message);
};

/**
 * Generic notification function
 * 
 * @param type - Type of notification
 * @param message - The message to display
 */
export const notify = (type: NotificationType, message: string): void => {
  openNotificationFn(type, message);
};

/**
 * Show a notification for a successful save operation
 * 
 * @param entityName - Name of the entity that was saved (e.g., "Vehicle", "Contact")
 */
export const notifySaveSuccess = (entityName: string): void => {
  openNotificationFn('success', `${entityName} saved successfully`);
};

/**
 * Show a notification for a successful delete operation
 * 
 * @param entityName - Name of the entity that was deleted
 */
export const notifyDeleteSuccess = (entityName: string): void => {
  openNotificationFn('success', `${entityName} deleted successfully`);
};

/**
 * Show a notification for a successful update operation
 * 
 * @param entityName - Name of the entity that was updated
 */
export const notifyUpdateSuccess = (entityName: string): void => {
  openNotificationFn('success', `${entityName} updated successfully`);
};

/**
 * Show a notification for a failed operation
 * 
 * @param operation - Name of the operation that failed (e.g., "Save", "Delete")
 * @param entityName - Name of the entity
 */
export const notifyOperationFailed = (operation: string, entityName: string): void => {
  openNotificationFn('error', `Failed to ${operation.toLowerCase()} ${entityName}`);
};

/**
 * Show a notification for validation errors
 * 
 * @param message - The validation error message
 */
export const notifyValidationError = (message: string): void => {
  openNotificationFn('error', `Validation Error: ${message}`);
};

/**
 * Show a notification for network errors
 * 
 * @param message - Optional custom message
 */
export const notifyNetworkError = (message?: string): void => {
  openNotificationFn('error', message || 'Network Error: Please check your connection and try again');
};
